import React, { useState } from "react";
import { Plus, Search, Filter, Users, Clock, CheckCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useArtistApplications, type ArtistApplication, type CreateArtistApplicationData, type UpdateArtistApplicationData } from "@/hooks/useArtistApplications";
import ArtistApplicationsTable from "./ArtistApplicationsTable";
import ArtistApplicationForm from "./ArtistApplicationForm";
import ViewArtistApplication from "./ViewArtistApplication";

export default function ArtistApplicationsManagement() {
  console.log("ArtistApplicationsManagement component rendering...");
  
  // Use the database hooks
  const { 
    artistApplications, 
    loading: applicationsLoading, 
    error: applicationsError, 
    createArtistApplication, 
    updateArtistApplication, 
    deleteArtistApplication 
  } = useArtistApplications();
  
  // Simple test to ensure component is being called
  React.useEffect(() => {
    console.log("ArtistApplicationsManagement component mounted");
  }, []);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewingApplication, setViewingApplication] = useState<ArtistApplication | null>(null);
  const [editingApplication, setEditingApplication] = useState<ArtistApplication | null>(null);
  const [isViewMode, setIsViewMode] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (data: CreateArtistApplicationData | UpdateArtistApplicationData) => {
    try {
      if (editingApplication) {
        const updatedApplication = await updateArtistApplication(data as UpdateArtistApplicationData);
        
        if (updatedApplication) {
          toast({
            title: "Success",
            description: "Artist application updated successfully",
          });
        } else {
          throw new Error("Failed to update artist application");
        }
      } else {
        const newApplication = await createArtistApplication(data as CreateArtistApplicationData);
        
        if (newApplication) {
          toast({
            title: "Success",
            description: "Artist application created successfully",
          });
        } else {
          throw new Error("Failed to create artist application");
        }
      }

      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error("Error saving artist application:", error);
      toast({
        title: "Error",
        description: "Failed to save artist application",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (applicationId: string) => {
    if (!confirm("Are you sure you want to delete this artist application?")) return;

    try {
      const success = await deleteArtistApplication(applicationId);
      
      if (success) {
        toast({
          title: "Success",
          description: "Artist application deleted successfully",
        });
      } else {
        throw new Error("Failed to delete artist application");
      }
    } catch (error) {
      console.error("Error deleting artist application:", error);
      toast({
        title: "Error",
        description: "Failed to delete artist application",
        variant: "destructive",
      });
    }
  };

  const handleView = (application: ArtistApplication) => {
    setViewingApplication(application);
    setIsViewMode(true);
    setIsDialogOpen(true);
  };

  const handleEdit = (application: ArtistApplication) => {
    setEditingApplication(application);
    setIsViewMode(false);
    setIsDialogOpen(true);
  };

  const handleStatusChange = async (applicationId: string, status: ArtistApplication['status']) => {
    try {
      const success = await updateArtistApplication({
        id: applicationId,
        status,
      });
      
      if (success) {
        toast({
          title: "Success",
          description: `Application status updated to ${status}`,
        });
      } else {
        throw new Error("Failed to update application status");
      }
    } catch (error) {
      console.error("Error updating application status:", error);
      toast({
        title: "Error",
        description: "Failed to update application status",
        variant: "destructive",
      });
    }
  };

  const resetForm = () => {
    setEditingApplication(null);
    setViewingApplication(null);
    setIsViewMode(false);
  };

  const filteredApplications = artistApplications.filter((application) => {
    const matchesSearch = 
      application.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      application.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      application.art_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      application.bio.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || application.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const loading = applicationsLoading;

  // Show error if any
  if (applicationsError) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Error Loading Data</h2>
          <p className="text-gray-600">
            {applicationsError}
          </p>
        </div>
      </div>
    );
  }

  const pendingCount = artistApplications.filter(app => app.status === 'pending').length;
  const reviewedCount = artistApplications.filter(app => app.status === 'reviewed').length;
  const approvedCount = artistApplications.filter(app => app.status === 'approved').length;
  const rejectedCount = artistApplications.filter(app => app.status === 'rejected').length;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Artist Applications Management</h1>
        <p className="text-gray-600 mt-2">Manage artist applications and collaborations</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Applications</CardTitle>
            <Users className="h-4 w-4 text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{artistApplications.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Clock className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{pendingCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Reviewed</CardTitle>
            <Clock className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{reviewedCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Approved</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{approvedCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Rejected</CardTitle>
            <XCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{rejectedCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search applications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Filter className="h-4 w-4 text-gray-400" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="reviewed">Reviewed</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => resetForm()}>
              <Plus className="h-4 w-4 mr-2" />
              Add Application
            </Button>
          </DialogTrigger>
          <DialogContent className={`${isViewMode ? 'max-w-6xl' : 'max-w-4xl'} max-h-[90vh] overflow-y-auto`}>
            <DialogHeader>
              <DialogTitle>
                {isViewMode && viewingApplication 
                  ? `View Artist Application - ${viewingApplication.full_name}`
                  : editingApplication 
                    ? "Edit Artist Application" 
                    : "Add New Artist Application"
                }
              </DialogTitle>
              <DialogDescription>
                {isViewMode && viewingApplication 
                  ? "View complete artist application details"
                  : editingApplication 
                    ? "Update artist application information" 
                    : "Create a new artist application"
                }
              </DialogDescription>
            </DialogHeader>
            {isViewMode && viewingApplication ? (
              <ViewArtistApplication
                application={viewingApplication}
                onClose={() => setIsDialogOpen(false)}
                onEdit={() => {
                  setEditingApplication(viewingApplication);
                  setIsViewMode(false);
                }}
              />
            ) : (
              <ArtistApplicationForm
                application={editingApplication}
                onSubmit={handleSubmit}
                onCancel={() => setIsDialogOpen(false)}
                loading={loading}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Applications Table */}
      <Card>
        <CardHeader>
          <CardTitle>Artist Applications ({filteredApplications.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading applications...</div>
          ) : (
            <ArtistApplicationsTable
              applications={filteredApplications}
              onView={handleView}
              onDelete={handleDelete}
              onStatusChange={handleStatusChange}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
